import { hasPermission } from '@crm/shared'
import { useInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Activity } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge, Card, Skeleton } from '@/components/ui/field'
import { api } from '@/lib/api'
import { fetchMe } from '@/lib/auth-query'
import { formatDateTime } from '@/lib/utils'

export const Route = createFileRoute('/_app/audit')({
  beforeLoad: async ({ context }) => {
    const me = await fetchMe(context.queryClient)
    if (me && !hasPermission(me.user.role, 'audit:view')) {
      throw redirect({ to: '/' })
    }
  },
  component: AuditPage,
})

interface AuditEntry {
  id: string
  action: string
  entity: string | null
  entityId: string | null
  diff: Record<string, unknown> | null
  ip: string | null
  createdAt: string
  userName: string | null
  userEmail: string | null
}

interface AuditPage {
  items: AuditEntry[]
  nextCursor: string | null
}

function AuditPage() {
  // Cursor pagination, matching the API. Same pattern the client list uses in Phase 2.
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useInfiniteQuery({
      queryKey: ['audit'],
      queryFn: ({ pageParam }) =>
        api.get<AuditPage>(
          `/api/audit?limit=50${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
        ),
      initialPageParam: '',
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    })

  const entries = data?.pages.flatMap((p) => p.items) ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Audit log</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Every sign-in and change, newest first.
        </p>
      </div>

      <Card className="overflow-hidden">
        {isLoading && (
          <div className="space-y-2 p-6">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        )}
        {isError && (
          <p className="p-6 text-sm text-[var(--color-destructive)]">
            Couldn&rsquo;t load the audit log.
          </p>
        )}
        {!isLoading && entries.length === 0 && (
          <div className="flex flex-col items-center gap-2 p-6 text-center">
            <Activity className="h-6 w-6 text-[var(--color-muted-foreground)]" />
            <p className="text-sm text-[var(--color-muted-foreground)]">Nothing recorded yet.</p>
          </div>
        )}

        {entries.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Who</th>
                  <th className="px-4 py-2 font-medium">Action</th>
                  <th className="px-4 py-2 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-b border-[var(--color-border)] last:border-0"
                  >
                    <td className="whitespace-nowrap px-4 py-2 text-[var(--color-muted-foreground)]">
                      {formatDateTime(entry.createdAt)}
                    </td>
                    <td className="px-4 py-2">
                      {entry.userName ?? (
                        <span className="text-[var(--color-muted-foreground)]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={entry.action.endsWith('_failed') ? 'destructive' : 'muted'}>
                        {entry.action}
                      </Badge>
                    </td>
                    <td className="max-w-md px-4 py-2 text-xs text-[var(--color-muted-foreground)]">
                      {entry.diff ? (
                        <code className="break-all">{JSON.stringify(entry.diff)}</code>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            loading={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      )}
    </div>
  )
}
